import React from 'react'
import { Box, Card, CardMedia, Paper, Stack, Typography } from '@mui/material'
import { useGlobalState } from 'state'
import { Legend, ObservatoryType } from 'common/types'
import { styled } from '@mui/material/styles'
import { MenuLegends, RainObservatoryLegend, StageObservatoryLegend, SuijinObservatoryLegend, UnderpassObservatoryLegend } from 'common/consts'
import { LegendRoadRegulation } from './LegendRoadRegulation'
import { green, grey, purple, red } from '@mui/material/colors'


type ObservatoryIconProps = {
  color: string
  strokeColor?: string
  strokeWidth?: number
}

const RainIcon = ({ color, strokeColor = grey[600], strokeWidth = 2 }: ObservatoryIconProps) => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" shapeRendering="geometricPrecision">
      <circle
        cx="24"
        cy="24"
        r="20"
        fill={color}
        opacity="1"
        stroke={strokeColor}
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={strokeWidth}
      />
    </svg>
  )
}

const StageIcon = ({ color, strokeColor = grey[600], strokeWidth = 2 }: ObservatoryIconProps) => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" shapeRendering="geometricPrecision">
      <path
        d="M2 43L24.055 5L46 43L2 43Z"
        fill={color}
        fillRule="nonzero"
        opacity="1"
        stroke={strokeColor}
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={strokeWidth}
      />
    </svg>
  )
}

const SuijinIcon = ({ color, strokeColor = grey[600], strokeWidth = 2 }: ObservatoryIconProps) => {
  return (
    <svg height="48.0pt" version="1.1" viewBox="0 0 48 48" width="48.0pt" xmlns="http://www.w3.org/2000/svg">
      <g opacity="1">
        <path
          d="M24 2C11.856 2 2 11.856 2 24C2 36.144 11.856 46 24 46C36.144 46 46 36.144 46 24C46 11.856 36.144 2 24 2Z"
          fill={color}
          fillRule="nonzero"
          opacity="1"
          stroke={strokeColor}
          strokeLinecap="butt"
          strokeLinejoin="round"
          strokeWidth={strokeWidth}
        />
        <path
          d="M14.096 29.312C14.288 30.624 14.416 31.232 14.704 31.968C15.312 33.536 16.24 34.752 17.52 35.616C19.056 36.64 21.616 37.28 24.24 37.28C27.664 37.28 30.192 36.48 31.888 34.848C33.2 33.536 33.904 31.68 33.904 29.408C33.904 27.008 33.072 25.248 31.248 23.904C30.096 23.104 28.56 22.496 26.032 21.952C22.48 21.152 22.224 21.088 21.52 20.8C20.048 20.16 19.344 19.232 19.344 17.888C19.344 15.808 21.232 14.4 24.016 14.4C25.776 14.4 27.184 14.944 28.016 15.936C28.56 16.576 28.784 17.12 28.976 18.208L33.552 17.664C33.168 15.968 32.784 15.008 31.984 13.952C30.416 11.84 27.664 10.72 24.016 10.72C18.512 10.72 14.928 13.6 14.928 18.048C14.928 20.192 15.76 21.952 17.392 23.232C18.544 24.128 19.824 24.64 22.224 25.184C26.64 26.208 26.832 26.24 27.792 26.752C28.784 27.296 29.392 28.384 29.392 29.696C29.392 32.096 27.472 33.536 24.272 33.536C22.192 33.536 20.528 32.864 19.6 31.648C19.024 30.912 18.832 30.272 18.704 28.832L14.096 29.312Z"
          fill="#ffffff"
          fillRule="nonzero"
          opacity="1"
          strokeWidth={0}
        />
      </g>
    </svg>
  )
}

export const UnderpassIcon = ({ color, strokeColor = grey[800], strokeWidth = 1 }: ObservatoryIconProps) => {
  return (
    <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <clipPath><rect height="24" width="24" x="0" y="0"/></clipPath>
      <path
        d="M19 3L5 3C3.9 3 3 3.9 3 5L3 19C3 20.1 3.9 21 5 21L19 21C20.1 21 21 20.1 21 19L21 5C21 3.9 20.1 3 19 3Z"
        fill={color} fillRule="nonzero" opacity="1" stroke={strokeColor} strokeLinecap="butt" strokeLinejoin="miter" strokeWidth={strokeWidth}
      />
      <path
        d="M18.1066 5.8316C17.9301 5.31095 17.436 4.94032 16.8535 4.94032L7.14647 4.94032C6.56404 4.94032 6.07869 5.31095 5.89337 5.8316L4.05786 11.1175L4.05786 18.1772C4.05786 18.6626 4.45496 19.0597 4.94032 19.0597L5.82278 19.0597C6.30813 19.0597 6.70524 18.6626 6.70524 18.1772L6.70524 17.2948L17.2948 17.2948L17.2948 18.1772C17.2948 18.6626 17.6919 19.0597 18.1772 19.0597L19.0597 19.0597C19.545 19.0597 19.9421 18.6626 19.9421 18.1772L19.9421 11.1175L18.1066 5.8316ZM7.14647 14.6474C6.41403 14.6474 5.82278 14.0561 5.82278 13.3237C5.82278 12.5912 6.41403 12 7.14647 12C7.87891 12 8.47016 12.5912 8.47016 13.3237C8.47016 14.0561 7.87891 14.6474 7.14647 14.6474ZM16.8535 14.6474C16.1211 14.6474 15.5298 14.0561 15.5298 13.3237C15.5298 12.5912 16.1211 12 16.8535 12C17.586 12 18.1772 12.5912 18.1772 13.3237C18.1772 14.0561 17.586 14.6474 16.8535 14.6474ZM5.82278 10.2351L7.14647 6.26401L16.8535 6.26401L18.1772 10.2351L5.82278 10.2351Z"
        fill={strokeColor} fillRule="nonzero" opacity="1" stroke="none"
      />
      <path
        d="M0.5 15.4733L23.5 15.575"
        fill="none" opacity="1" stroke={strokeColor} strokeLinecap="butt" strokeLinejoin="miter" strokeWidth={strokeWidth}
      />
    </svg>
  )
}


const LegendTitle = ({title}: {title: string | undefined}) => {
  if (!title) return <></>
  return (
    <Typography sx={{
      fontSize: 11,
      lineHeight: 1.2,
      textAlign: 'right',
      fontWeight: 'bold',
      whiteSpace: 'pre-wrap',
      color: grey[800],
      textShadow: '1px 1px 0 #ffffff, -1px 1px 0 #ffffff, 1px -1px 0 #ffffff, -1px -1px 0 #ffffff',
    }}>
      {title}
    </Typography>
  )
}

const SpotLegendText = styled(Typography)(() => ({
  fontSize: 11,
  color: grey[900],
  fontWeight: 'normal'
}))

export const LegendPanel = ({legend, obsType}: {legend: Legend, obsType?: ObservatoryType}) => {
  const iconSize = 14
  return (
    <Stack alignItems={'flex-end'}>
      <LegendTitle title={legend.title} />
      { legend.colors && (
        <Stack component={Card} bgcolor={'rgba(250, 250, 250, 0.95)'} sx={{ pointerEvents: 'painted'}}>
          <Stack py={'2px'} px={'6px'} spacing={0}>
            { legend.colors.map((c, index) => {
              return (
                <Stack direction={'row'} spacing={1} alignItems={'center'} key={index}>
                  { obsType === ObservatoryType.Rain && <Box width={iconSize} height={iconSize} display={'flex'} alignItems={'center'} justifyContent={'center'}><RainIcon color={c.color} strokeColor={grey[800]} strokeWidth={1} /></Box> }
                  { obsType === ObservatoryType.Stage && <Box width={iconSize} height={iconSize} display={'flex'} alignItems={'center'} justifyContent={'center'}><StageIcon color={c.color} strokeColor={grey[600]} strokeWidth={2} /></Box> }
                  { obsType === ObservatoryType.Suijin && <Box width={iconSize} height={iconSize} display={'flex'} alignItems={'center'} justifyContent={'center'}><SuijinIcon color={c.color} strokeColor={grey[600]} strokeWidth={2} /></Box> }
                  { obsType === ObservatoryType.Underpass && <Box width={iconSize} height={iconSize} display={'flex'} alignItems={'center'} justifyContent={'center'}><UnderpassIcon color={c.color} strokeColor={grey[800]} strokeWidth={1} /></Box> }
                  { obsType === undefined && <Box width={iconSize} height={iconSize} bgcolor={c.color} border={'1px solid ' + grey[400]}></Box> }
                  <Typography variant='caption' fontSize={11} whiteSpace={'nowrap'}>{c.value}</Typography>
                </Stack>
              )
            }) }
          </Stack>
        </Stack>
      )}
      { legend.image !== undefined && (
        <Card sx={{maxWidth: 40, maxHeight: 30}}>
          <CardMedia component="img" src={legend.image} />
        </Card>
      )}
    </Stack>
  )
}

export const LegendControl = () => {
  const state = useGlobalState()
  const [menuLegends, setMenuLegends] = React.useState<Legend[]>([])

  React.useEffect(() => {
    setMenuLegends(MenuLegends[state.app.menu])
  }, [state.app.menu])

  const isRiverFloods = React.useMemo(() => {
    return state.app.menu === 'river-floods'
  }, [state.app.menu])

  return (
    <Stack direction={'column'} alignItems={'flex-end'} spacing={1}>
      <Stack direction={'row'} spacing={1}>
        { state.app.observatoryTypes.includes(ObservatoryType.Suijin) && <LegendPanel legend={SuijinObservatoryLegend} obsType={ObservatoryType.Suijin}  /> }
        { state.app.observatoryTypes.includes(ObservatoryType.Rain) && <LegendPanel legend={RainObservatoryLegend} obsType={ObservatoryType.Rain}  /> }
        { state.app.observatoryTypes.includes(ObservatoryType.Stage) && <LegendPanel legend={StageObservatoryLegend} obsType={ObservatoryType.Stage}  /> }
        { state.app.observatoryTypes.includes(ObservatoryType.Underpass) && <LegendPanel legend={UnderpassObservatoryLegend} obsType={ObservatoryType.Underpass}  /> }
      </Stack>
      <Stack direction={'row'} spacing={1}>
        <Stack direction={'row'} spacing={1}>
          { menuLegends.map((legend: Legend, index: number) => <LegendPanel legend={legend} key={index} />) }
        </Stack>
        <LegendRoadRegulation />
      </Stack>
      { isRiverFloods && (
        <Paper elevation={1} sx={{backgroundColor: 'rgba(250,250,250,0.95)', p: 1, m: 0, width: 100}}>
          <Stack direction={'row'} spacing={1} alignItems={'center'}>
            <Box width={20} height={5} borderTop={`5px solid ${red[500]}`} />
            <SpotLegendText>高齢者避難</SpotLegendText>
          </Stack>
          <Stack direction={'row'} spacing={1} alignItems={'center'}>
            <Box width={20} height={10} borderTop={`5px solid ${red[500]}`} borderBottom={`5px solid ${purple[500]}`} />
            <SpotLegendText>避難指示</SpotLegendText>
          </Stack>
          <Stack direction={'row'} spacing={1} alignItems={'center'}>
            <Box width={20} height={10} borderTop={`5px solid ${red[500]}`} borderBottom={`5px solid ${grey[900]}`} />
            <SpotLegendText>緊急避難</SpotLegendText>
          </Stack>
        </Paper>
      )}
      <Paper elevation={1} sx={{backgroundColor: 'rgba(250,250,250,0.95)', p: 1, m: 0, width: 100}}>
        <Stack direction={'row'} spacing={1} alignItems={'center'}>
          <Box width={20} height={20} border={`3px solid ${green[500]}`} />
          <SpotLegendText>巴川流域</SpotLegendText>
        </Stack>
      </Paper>
    </Stack>
  )
}
